import {useMemo} from 'react';
import type {ViewStyle} from 'react-native';
import {Platform, StyleSheet, Text, View} from 'react-native';

import BackgroundView from '../components/BackgroundView';
import ContentScrollView from '../components/ContentScrollView';
import DatePillText from '../components/DatePillText';
import {variance} from '../styling';

export type ShowInsightScreenProps = {
  today: string | null;
  compensateHeaderHeight?: number;
  quoteBody: string;
  quoteSource: string;
  topicBody: string;
};

export default function ShowInsightScreen(props: ShowInsightScreenProps) {
  const {today, compensateHeaderHeight, quoteBody, quoteSource, topicBody} =
    props;
  const contentContainerStyle: ViewStyle = useMemo(
    () => ({
      overflow: Platform.OS === 'web' ? 'hidden' : 'visible',
      paddingTop: compensateHeaderHeight ?? 0,
    }),
    [compensateHeaderHeight],
  );
  const topIsCompensated = compensateHeaderHeight !== undefined;
  return (
    <ContentScrollView
      contentContainerStyle={contentContainerStyle}
      topIsCompensated={topIsCompensated}>
      <BackgroundView />
      {today !== null && (
        <DatePillText style={layoutStyles.center}>{today}</DatePillText>
      )}
      <View style={layoutStyles.delimiter} />
      <QuoteBodyText style={layoutStyles.quoteBody}>{quoteBody}</QuoteBodyText>
      <QuoteSourceText style={layoutStyles.quoteSource}>
        {quoteSource}
      </QuoteSourceText>
      <View style={layoutStyles.delimiter} />
      <TopicBodyText style={layoutStyles.topicBody}>{topicBody}</TopicBodyText>
      <View style={layoutStyles.delimiter} />
    </ContentScrollView>
  );
}

const MAX_WIDTH = 800;

const layoutStyles = StyleSheet.create({
  center: {
    alignSelf: 'center',
  },
  delimiter: {
    minHeight: 30,
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
  },
  quoteBody: framedHorizontally(),
  quoteSource: {
    marginTop: 8,
    ...framedHorizontally(),
  },
  topicBody: framedHorizontally(40),
});

function framedHorizontally(padding = 44): ViewStyle {
  return {
    alignSelf: 'stretch',
    paddingHorizontal: padding,
    maxWidth: MAX_WIDTH,
    marginLeft: 'auto',
    marginRight: 'auto',
  };
}

const QuoteBodyText = variance(Text)(theme => ({
  root: {
    textAlign: 'center',
    ...theme.text('quoteBody'),
    color: theme.palette.textPrimary,
    fontSize: 16,
    lineHeight: 20,
  },
}));

const QuoteSourceText = variance(Text)(theme => ({
  root: {
    textAlign: 'center',
    ...theme.text(),
    color: theme.palette.textPrimary,
    fontSize: 11,
    lineHeight: 14,
  },
}));

const TopicBodyText = variance(Text)(theme => ({
  root: {
    textAlign: 'justify',
    ...theme.text(),
    color: theme.palette.textPrimary,
    fontSize: 16,
    lineHeight: 22,
  },
}));
